import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { getErrorMessage } from '../../utils/errorHandler';
import { useObjectShareCounterpartiesQuery, useObjectSharesQuery } from './objectShares.queries';
import {
  useGrantObjectSharesMutation,
  useRevokeObjectShareMutation,
} from './objectShares.mutations';
import {
  classifyShareError,
  isBenignShareOutcome,
  isForbiddenError,
} from '../flight-shares/shareErrors';
import {
  counterpartyName,
  isLiveShare,
  isOutboundShare,
  shareOutcomeMessage,
} from '../flight-shares/shareDisplay';
import { pendingTargetId } from '../../utils/pendingTargetId';
import { formatDate } from '../file-browser/utils';
import type { ObjectSharePermission } from '../../types/api';

interface ObjectShareDialogProps {
  /**
   * The registry rows to manage sharing for. Empty closes the dialog.
   *
   * A list, not one id, because the Files tab is multi-select and the API has
   * taken `object_ids` (up to 500) since this shipped — the single-id version
   * silently shared only the right-clicked item out of a selection.
   */
  objectIds: string[];
  /** Shown in the title so the user knows which file or folder this is. */
  label?: string;
  /** Whether the selection contains at least one folder. */
  isFolder?: boolean;
  onClose: () => void;
}

/**
 * Share / unshare files and folders — the file browser's ONE right-click share
 * surface (#1182 Stage 4 slice 4; sole survivor since STD-029 R-12 / B-6).
 *
 * A dialog rather than two menu items ("Share" / "Unshare"), because sharing
 * needs a grantee and unsharing needs to know *which* grant to withdraw. A menu
 * that could label itself correctly would have to fetch the object's share list
 * on every right-click; this fetches once, on open, and does both.
 *
 * It kept the shape of the retired per-file `FlightShareDialog` and reuses the
 * pure helpers still living in `flight-shares/` (`shareErrors`, `shareDisplay`,
 * `pendingTargetId`) rather than copying them — those take structural types, so
 * they never depended on which table the share was in. `ShareDroneFlightsDialog`
 * is the other dialog built to this shape and is still live.
 *
 * The one thing this dialog says that a flight grant cannot: for a folder, the
 * grant covers files that do not exist yet. That is the capability the whole
 * registry was built for, and a user who does not know it would either
 * re-share every new file or assume the folder grant had silently stopped
 * working.
 *
 * **With more than one object selected it becomes grant-only.** "Current
 * access" and Revoke are per-object questions: across a mixed selection there
 * is no single honest answer, and rendering the first object's shares next to a
 * control that grants to all of them would actively mislead. Granting is
 * unambiguous — one call, N ids, and `skipped_existing` covers the ones that
 * already had it.
 */
export const ObjectShareDialog: React.FC<ObjectShareDialogProps> = ({
  objectIds,
  label,
  isFolder = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const open = objectIds.length > 0;
  const single = objectIds.length === 1 ? objectIds[0] : null;

  const [granteeId, setGranteeId] = useState<string>('');
  const [permission, setPermission] = useState<ObjectSharePermission>('read');

  const counterpartiesQuery = useObjectShareCounterpartiesQuery(open);
  // Only fetched for a single object — see the grant-only note above.
  const sharesQuery = useObjectSharesQuery(
    { objectId: single ?? undefined },
    open && single !== null
  );
  const grant = useGrantObjectSharesMutation();
  const revoke = useRevokeObjectShareMutation();

  const counterparties = counterpartiesQuery.data;
  const shares = useMemo(() => (sharesQuery.data ?? []).filter(isLiveShare), [sharesQuery.data]);

  // Orgs that already hold a live share are removed from the picker rather than
  // left selectable-and-rejected: `uq_object_shares_active` makes a second live
  // grant to the same org impossible, so offering it would only produce a 409.
  //
  // Only for a single object. Across a selection the same org may hold a share
  // on some and not others, and filtering it out would make the remainder
  // unshareable; the grant is idempotent and reports `skipped_existing`, so
  // offering every legal counterparty is both correct and the only option that
  // can express "share the rest of these too".
  const availableCounterparties = useMemo(() => {
    const all = counterparties?.organizations ?? [];
    if (single === null) return all;
    const taken = new Set(shares.map((s) => s.grantee_organization_id));
    return all.filter((o) => !taken.has(o.id));
  }, [counterparties, shares, single]);

  const handleClose = () => {
    setGranteeId('');
    setPermission('read');
    grant.reset();
    revoke.reset();
    onClose();
  };

  const handleGrant = async () => {
    if (objectIds.length === 0 || !granteeId) return;
    try {
      const result = await grant.mutateAsync({
        grantee_organization_id: granteeId,
        permission,
        object_ids: objectIds,
      });
      // `skipped_existing` is reported rather than swallowed: after a partial
      // selection it is the difference between "nothing happened" and "the
      // remainder landed", and silently showing `granted` alone would read as a
      // failure when re-sharing a selection that was mostly already shared.
      showSuccess(
        shareOutcomeMessage(t, result, {
          sharedOne: 'objectShares.sharedOne',
          sharedOther: 'objectShares.sharedOther',
          skippedOne: 'objectShares.skippedOne',
          skippedOther: 'objectShares.skippedOther',
        })
      );
      setGranteeId('');
    } catch {
      // Rendered inline below — this dialog opts out of the global toast so the
      // failure stays on screen next to the control that caused it.
    }
  };

  const handleRevoke = async (shareId: string, orgLabel: string) => {
    const confirmKey = isFolder ? 'objectShares.revokeConfirmFolder' : 'objectShares.revokeConfirm';
    if (!window.confirm(t(confirmKey, { org: orgLabel }))) return;
    try {
      await revoke.mutateAsync(shareId);
      showSuccess(t('objectShares.revokeSuccess'));
    } catch {
      // Rendered inline below.
    }
  };

  const grantError = grant.error ? classifyShareError(grant.error) : null;

  // Which ROW is being revoked (#1232) — the same fix the Shared view applies,
  // via the same shared helper. `revoke.isPending` belongs to the LIST, not to a row, so
  // wiring it straight to every button disabled all of them on the first click.
  // `pendingTargetId` checks `isPending` before reading `variables` because
  // react-query keeps the last `variables` after settle; see its docstring.
  const revokingShareId = pendingTargetId(revoke.isPending, revoke.variables);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="object-share-dialog"
    >
      <DialogTitle>
        {single !== null
          ? t('objectShares.dialogTitle', { name: label ?? '' })
          : t('objectShares.dialogTitleMany', { count: objectIds.length })}
      </DialogTitle>
      <DialogContent dividers>
        {(sharesQuery.isLoading || counterpartiesQuery.isLoading) && <LinearProgress />}

        {/* Say how many are in scope. Without it the only cue that this grant
            covers a selection rather than the right-clicked item is the title,
            and the whole reason this dialog changed shape is that a user could
            not tell the difference. */}
        {single === null && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="object-share-multi-note">
            {t('objectShares.multiSelection', { count: objectIds.length })}
          </Alert>
        )}

        {/* The one claim this dialog makes that the flight one cannot. Shown
            before anything else because it changes what the user is deciding:
            they are granting access to a growing set, not to N files. */}
        {isFolder && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="object-share-folder-note">
            {t('objectShares.folderCoversFutureFiles')}
          </Alert>
        )}

        {/* The counterparties call doubles as the visible org-admin gate — but
            only a 403 means "you are not an admin". Any other failure gets the
            real message, or a real admin is told to go ask for permissions they
            already hold. */}
        {counterpartiesQuery.isError &&
          (isForbiddenError(counterpartiesQuery.error) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('objectShares.onlyAdmins')}
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getErrorMessage(counterpartiesQuery.error)}
            </Alert>
          ))}

        {/* Everything from here to the divider is the per-object half, and it
            is rendered ONLY for a single object. "Current access" and Revoke
            answer questions about one row; across a selection whose members may
            have different grants there is no honest answer, and showing one
            object's would be worse than showing none. */}
        {single !== null && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('objectShares.currentShares')}
            </Typography>

            {sharesQuery.isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {getErrorMessage(sharesQuery.error)}
              </Alert>
            )}

            {/* The list is driven by `sharesQuery`, which is NOT admin-gated and can
            succeed on its own. Only the *direction* of each row — its label, and
            whether Revoke is offered — needs the caller's own org id from the
            counterparties call, so only that is withheld when it is in flight or
            has failed. `directionKnown === false` renders a neutral label and no
            Revoke button: we will not claim a direction we cannot derive, and we
            will not offer a control only the granting side can use. */}
            {shares.length > 0 ? (
              <List dense data-testid="object-share-list">
                {shares.map((share) => {
                  const outbound = counterparties
                    ? isOutboundShare(share, counterparties.own_organization_id)
                    : null;
                  const orgLabel =
                    counterpartyName(share, counterparties) ?? t('objectShares.unknownOrg');
                  return (
                    <ListItem
                      key={share.id}
                      data-testid="object-share-row"
                      secondaryAction={
                        outbound === true ? (
                          <Button
                            size="small"
                            color="error"
                            disabled={revokingShareId === share.id}
                            onClick={() => handleRevoke(share.id, orgLabel)}
                            data-testid="object-share-revoke-btn"
                            // Which row this button belongs to, for the browser
                            // spec that pins per-row gating (#1312). The testid
                            // is shared by every row by design — one row is
                            // addressed by its share id, the way a file card is
                            // addressed by `data-file-key`.
                            data-share-id={share.id}
                          >
                            {t('objectShares.revoke')}
                          </Button>
                        ) : undefined
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {outbound === null
                              ? t('objectShares.sharedDirectionUnknown')
                              : outbound
                                ? t('objectShares.sharedWithOrg', { org: orgLabel })
                                : t('objectShares.sharedByOrg', { org: orgLabel })}
                            <Chip
                              size="small"
                              label={
                                share.permission === 'write'
                                  ? t('objectShares.permissionWrite')
                                  : t('objectShares.permissionRead')
                              }
                            />
                          </Box>
                        }
                        secondary={t('objectShares.grantedOn', {
                          date: formatDate(share.granted_at),
                        })}
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              /* "Not shared with anyone" is a claim about the data, so it is made
             only when the fetch actually succeeded. On failure `shares` is also
             empty, and asserting the object is unshared there would be stating
             an unknown as a fact — next to an error saying we could not look. */
              sharesQuery.isSuccess && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('objectShares.noShares')}
                </Typography>
              )
            )}

            {revoke.isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {getErrorMessage(revoke.error)}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Typography variant="subtitle2">{t('objectShares.shareWith')}</Typography>

        {counterpartiesQuery.isSuccess && availableCounterparties.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('objectShares.noCounterparties')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 220, flex: 1 }} size="small">
              <InputLabel id="object-share-grantee-label">
                {t('objectShares.selectOrganization')}
              </InputLabel>
              <Select
                labelId="object-share-grantee-label"
                label={t('objectShares.selectOrganization')}
                value={granteeId}
                onChange={(e) => setGranteeId(e.target.value)}
              >
                {availableCounterparties.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="object-share-permission-label">
                {t('objectShares.permission')}
              </InputLabel>
              <Select
                labelId="object-share-permission-label"
                label={t('objectShares.permission')}
                value={permission}
                onChange={(e) => setPermission(e.target.value as ObjectSharePermission)}
              >
                <MenuItem value="read">{t('objectShares.permissionRead')}</MenuItem>
                <MenuItem value="write">{t('objectShares.permissionWrite')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {isFolder ? t('objectShares.permissionHelpFolder') : t('objectShares.permissionHelp')}
        </Typography>

        {grantError && (
          <Alert
            severity={isBenignShareOutcome(grantError) ? 'info' : 'error'}
            sx={{ mt: 2 }}
            data-testid="object-share-error"
          >
            {grantError.kind === 'illegalPairing'
              ? t('objectShares.errors.illegalPairing')
              : grantError.kind === 'noObjects'
                ? t('objectShares.errors.noObjects')
                : getErrorMessage(grant.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.close')}</Button>
        <Button
          variant="contained"
          disabled={!granteeId || grant.isPending}
          onClick={handleGrant}
          startIcon={grant.isPending ? <CircularProgress size={16} /> : undefined}
          data-testid="object-share-submit-btn"
        >
          {t('objectShares.share')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObjectShareDialog;
